import { useState, useEffect } from 'react';
import api from '../lib/api';

interface PricingRange {
  minGuests: number;
  maxGuests: number;
  basePrice: number;
  perGuest: number;
  perDay: number;
}

const defaultCookDineRanges: PricingRange[] = [
  { minGuests: 1, maxGuests: 4, basePrice: 400, perGuest: 80, perDay: 0 },
  { minGuests: 5, maxGuests: 20, basePrice: 550, perGuest: 65, perDay: 0 },
];

const defaultMealPrepRanges: PricingRange[] = [
  { minGuests: 1, maxGuests: 4, basePrice: 400, perGuest: 80, perDay: 150 },
  { minGuests: 5, maxGuests: 20, basePrice: 550, perGuest: 65, perDay: 130 },
];

function cloneRowCook(): PricingRange {
  return { minGuests: 1, maxGuests: 10, basePrice: 0, perGuest: 0, perDay: 0 };
}

function cloneRowMeal(): PricingRange {
  return { minGuests: 1, maxGuests: 10, basePrice: 0, perGuest: 0, perDay: 0 };
}

type MainTab = 'settings' | 'bookings' | 'payments' | 'analytics';

function idPart(id: unknown): string {
  if (id == null) return '—';
  const s =
    typeof id === 'object' && id !== null && '_id' in (id as object)
      ? String((id as { _id: unknown })._id)
      : String(id);
  return s.length > 10 ? `${s.slice(0, 8)}…` : s;
}

function ChefBookingsAdminTable() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState<unknown[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/admin/chef-bookings', {
          params: { page, limit, ...(status ? { status } : {}) },
        });
        setRows(res.data?.data ?? []);
        setTotal(res.data?.total ?? 0);
      } catch {
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, status]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">
          Status{' '}
          <select
            className="border rounded px-2 py-1 ml-1"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">All</option>
            <option value="pending_payment">pending_payment</option>
            <option value="pending">pending</option>
            <option value="accepted">accepted</option>
            <option value="rejected">rejected</option>
            <option value="completed">completed</option>
            <option value="cancelled">cancelled</option>
          </select>
        </label>
        <span className="text-sm text-gray-500">{total} total</span>
      </div>
      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Booking</th>
                <th className="p-2 text-left">Client</th>
                <th className="p-2 text-left">Chef</th>
                <th className="p-2 text-left">When</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-right">Total EGP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((raw) => {
                const r = raw as Record<string, unknown>;
                const user = r.userId as Record<string, unknown> | undefined;
                const chef = r.chefId as Record<string, unknown> | undefined;
                const pricing = r.pricing as Record<string, unknown> | undefined;
                const totalEgp = pricing?.totalEGP;
                return (
                  <tr key={String(r._id)} className="border-t">
                    <td className="p-2 font-mono text-xs">{idPart(r._id)}</td>
                    <td className="p-2">
                      {user?.name ? String(user.name) : '—'}
                      <div className="text-xs text-gray-500">{user?.email ? String(user.email) : ''}</div>
                    </td>
                    <td className="p-2">{chef?.name ? String(chef.name) : '—'}</td>
                    <td className="p-2">
                      {r.scheduledDate ? String(r.scheduledDate) : '—'}{' '}
                      {r.scheduledSlot ? String(r.scheduledSlot) : ''}
                    </td>
                    <td className="p-2">{r.status ? String(r.status) : '—'}</td>
                    <td className="p-2 text-right font-medium">
                      {typeof totalEgp === 'number' ? totalEgp.toFixed(2) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-2 items-center">
        <button
          type="button"
          className="px-3 py-1 border rounded disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          className="px-3 py-1 border rounded disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function ChefBookingPaymentsAdminTable() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState<unknown[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/admin/chef-booking-payments', {
          params: { page, limit, ...(status ? { status } : {}) },
        });
        setRows(res.data?.data ?? []);
        setTotal(res.data?.total ?? 0);
      } catch {
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, status]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">
          Status{' '}
          <select
            className="border rounded px-2 py-1 ml-1"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">All</option>
            <option value="pending">pending</option>
            <option value="completed">completed</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <span className="text-sm text-gray-500">{total} attempts</span>
      </div>
      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Session</th>
                <th className="p-2 text-left">Booking</th>
                <th className="p-2 text-left">User</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 text-left">Pay status</th>
                <th className="p-2 text-left">Gateway id</th>
                <th className="p-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((raw) => {
                const r = raw as Record<string, unknown>;
                const user = r.userId as Record<string, unknown> | undefined;
                const booking = r.bookingId as Record<string, unknown> | undefined;
                return (
                  <tr key={String(r._id)} className="border-t">
                    <td className="p-2 font-mono text-xs max-w-[140px] truncate" title={String(r.sessionId ?? '')}>
                      {r.sessionId ? String(r.sessionId).slice(0, 13) + '…' : '—'}
                    </td>
                    <td className="p-2 font-mono text-xs">{booking ? idPart(booking._id) : '—'}</td>
                    <td className="p-2">
                      {user?.name ? String(user.name) : '—'}
                      <div className="text-xs text-gray-500">{user?.email ? String(user.email) : ''}</div>
                    </td>
                    <td className="p-2 text-right">{r.amountEgp != null ? Number(r.amountEgp).toFixed(2) : '—'}</td>
                    <td className="p-2">{r.status ? String(r.status) : '—'}</td>
                    <td className="p-2 font-mono text-xs max-w-[100px] truncate">
                      {r.paymobGatewayTransactionId ? String(r.paymobGatewayTransactionId) : '—'}
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {r.createdAt ? new Date(String(r.createdAt)).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-2 items-center">
        <button
          type="button"
          className="px-3 py-1 border rounded disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          className="px-3 py-1 border rounded disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function ChefBookingAnalyticsPanel() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/admin/chef-bookings/analytics');
        setData((res.data?.data ?? null) as Record<string, unknown> | null);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-gray-500">Loading analytics…</p>;
  if (!data) return <p className="text-red-600">Could not load analytics.</p>;

  const byBook = (data.bookingsByStatus as Record<string, number>) || {};
  const byPay = (data.paymentsByStatus as Record<string, number>) || {};

  const stat = (label: string, value: string | number) => (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stat('Total bookings', Number(data.totalBookings ?? 0))}
        {stat('Paid bookings', Number(data.paidBookingsCount ?? 0))}
        {stat('Paid revenue (EGP)', Number(data.totalPaidRevenueEGP ?? 0).toFixed(2))}
        {stat('Unpaid pipeline (EGP)', Number(data.unpaidPipelineValueEGP ?? 0).toFixed(2))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stat('Payment attempts', Number(data.totalPaymentAttempts ?? 0))}
        {stat('New bookings (7 days)', Number(data.bookingsCreatedLast7Days ?? 0))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="font-semibold text-gray-800 mb-3">Bookings by status</h3>
          <ul className="text-sm space-y-1">
            {Object.keys(byBook).length === 0 ? (
              <li className="text-gray-500">No data</li>
            ) : (
              Object.entries(byBook).map(([k, v]) => (
                <li key={k} className="flex justify-between">
                  <span className="font-mono">{k}</span>
                  <span className="font-medium">{v}</span>
                </li>
              ))
            )}
          </ul>
        </div>
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="font-semibold text-gray-800 mb-3">Card transactions by status</h3>
          <ul className="text-sm space-y-1">
            {Object.keys(byPay).length === 0 ? (
              <li className="text-gray-500">No payment rows yet</li>
            ) : (
              Object.entries(byPay).map(([k, v]) => (
                <li key={k} className="flex justify-between">
                  <span className="font-mono">{k}</span>
                  <span className="font-medium">{v}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ChefOnDemandPricingSettings() {
  const [rangesCookDine, setRangesCookDine] = useState<PricingRange[]>([...defaultCookDineRanges]);
  const [rangesMealPrep, setRangesMealPrep] = useState<PricingRange[]>([...defaultMealPrepRanges]);
  const [termsEn, setTermsEn] = useState('');
  const [termsAr, setTermsAr] = useState('');
  const [commission, setCommission] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settings');
        const data = res.data?.data ?? res.data ?? {};
        const pr = data.chefOnDemandPricing;
        const mapCookDine = (arr: unknown): PricingRange[] => {
          if (!Array.isArray(arr) || arr.length === 0) return [...defaultCookDineRanges];
          return arr.map((r: PricingRange) => ({
            minGuests: Number(r.minGuests) || 1,
            maxGuests: Number(r.maxGuests) || 10,
            basePrice: Number(r.basePrice) || 0,
            perGuest: Number(r.perGuest) || 0,
            perDay: 0,
          }));
        };
        const mapMealPrep = (arr: unknown): PricingRange[] => {
          if (!Array.isArray(arr) || arr.length === 0) return [...defaultMealPrepRanges];
          return arr.map((r: PricingRange) => ({
            minGuests: Number(r.minGuests) || 1,
            maxGuests: Number(r.maxGuests) || 10,
            basePrice: Number(r.basePrice) || 0,
            perGuest: Number(r.perGuest) || 0,
            perDay: Number(r.perDay) || 0,
          }));
        };
        if (pr?.rangesCookDine && Array.isArray(pr.rangesCookDine) && pr.rangesCookDine.length > 0) {
          setRangesCookDine(mapCookDine(pr.rangesCookDine));
        } else if (pr?.ranges && Array.isArray(pr.ranges)) {
          setRangesCookDine(mapCookDine(pr.ranges));
        }
        if (pr?.rangesMealPrep && Array.isArray(pr.rangesMealPrep) && pr.rangesMealPrep.length > 0) {
          setRangesMealPrep(mapMealPrep(pr.rangesMealPrep));
        } else if (pr?.ranges && Array.isArray(pr.ranges)) {
          setRangesMealPrep(mapMealPrep(pr.ranges));
        }
        const t = data.chefOnDemandTerms;
        if (t && typeof t === 'object') {
          setTermsEn(String(t.en ?? ''));
          setTermsAr(String(t.ar ?? ''));
        }
        const c = data.chefPlatformCommissionPercent;
        if (typeof c === 'number') setCommission(c);
        else if (c != null) setCommission(parseFloat(String(c)) || 10);
      } catch {
        setMessage('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const savePricing = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/admin/settings/chefOnDemandPricing', {
        value: {
          rangesCookDine: rangesCookDine.map((r) => ({ ...r, perDay: 0 })),
          rangesMealPrep,
        },
      });
      setMessage('Pricing saved');
    } catch {
      setMessage('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveTerms = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/admin/settings/chefOnDemandTerms', {
        value: { en: termsEn, ar: termsAr },
      });
      setMessage('Terms & Conditions saved');
    } catch {
      setMessage('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveCommission = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/admin/settings/chefPlatformCommissionPercent', {
        value: Math.min(100, Math.max(0, commission)),
      });
      setMessage('Commission saved');
    } catch {
      setMessage('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (
    which: 'cook' | 'meal',
    i: number,
    field: keyof PricingRange,
    raw: string,
  ) => {
    const n = parseFloat(raw);
    const v = Number.isFinite(n) ? n : 0;
    const setter = which === 'cook' ? setRangesCookDine : setRangesMealPrep;
    setter((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: v };
      return next;
    });
  };

  const addRow = (which: 'cook' | 'meal') => {
    const setter = which === 'cook' ? setRangesCookDine : setRangesMealPrep;
    setter((prev) => [...prev, which === 'cook' ? cloneRowCook() : cloneRowMeal()]);
  };

  const removeRow = (which: 'cook' | 'meal', i: number) => {
    const setter = which === 'cook' ? setRangesCookDine : setRangesMealPrep;
    setter((prev) => (prev.length <= 1 ? prev : prev.filter((_, j) => j !== i)));
  };

  const renderRangeTable = (
    title: string,
    which: 'cook' | 'meal',
    ranges: PricingRange[],
    showPerDay: boolean,
  ) => (
    <div className="mb-8">
      <h3 className="text-md font-semibold mb-2 text-gray-800">{title}</h3>
      <p className="text-sm text-gray-500 mb-3">
        {showPerDay
          ? 'Base + (guests × per guest) + (days × per day). Chef discount and platform % apply in the app.'
          : 'Base + (guests × per guest) only. Chef discount and platform % apply in the app.'}
      </p>
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 text-left">Min guests</th>
              <th className="p-2 text-left">Max guests</th>
              <th className="p-2 text-left">Base (EGP)</th>
              <th className="p-2 text-left">Per guest</th>
              {showPerDay ? <th className="p-2 text-left">Per day</th> : null}
              <th className="p-2 w-20" />
            </tr>
          </thead>
          <tbody>
            {ranges.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="p-2">
                  <input
                    type="number"
                    className="border rounded px-2 py-1 w-20"
                    value={r.minGuests}
                    onChange={(e) => updateRow(which, i, 'minGuests', e.target.value)}
                  />
                </td>
                <td className="p-2">
                  <input
                    type="number"
                    className="border rounded px-2 py-1 w-20"
                    value={r.maxGuests}
                    onChange={(e) => updateRow(which, i, 'maxGuests', e.target.value)}
                  />
                </td>
                <td className="p-2">
                  <input
                    type="number"
                    className="border rounded px-2 py-1 w-24"
                    value={r.basePrice}
                    onChange={(e) => updateRow(which, i, 'basePrice', e.target.value)}
                  />
                </td>
                <td className="p-2">
                  <input
                    type="number"
                    className="border rounded px-2 py-1 w-24"
                    value={r.perGuest}
                    onChange={(e) => updateRow(which, i, 'perGuest', e.target.value)}
                  />
                </td>
                {showPerDay ? (
                  <td className="p-2">
                    <input
                      type="number"
                      className="border rounded px-2 py-1 w-24"
                      value={r.perDay}
                      onChange={(e) => updateRow(which, i, 'perDay', e.target.value)}
                    />
                  </td>
                ) : null}
                <td className="p-2">
                  <button
                    type="button"
                    className="text-red-600 text-sm disabled:opacity-40"
                    disabled={ranges.length <= 1}
                    onClick={() => removeRow(which, i)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="mt-2 text-sm text-orange-700 font-medium" onClick={() => addRow(which)}>
        + Add range
      </button>
    </div>
  );

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="space-y-8">
      {message && <p className="text-sm text-gray-600">{message}</p>}

      <section>
        <h2 className="text-lg font-semibold mb-1">Pricing ranges</h2>
        <p className="text-sm text-gray-500 mb-4">
          Cook &amp; dine is priced by guests only. Meal prep adds a per-day component.
        </p>
        {renderRangeTable('Cook & dine', 'cook', rangesCookDine, false)}
        {renderRangeTable('Meal prep', 'meal', rangesMealPrep, true)}
        <button
          type="button"
          className="px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50"
          disabled={saving}
          onClick={savePricing}
        >
          Save pricing
        </button>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Platform commission (%)</h2>
        <p className="text-sm text-gray-500 mb-2">Added to the user&apos;s total at checkout (after chef discount).</p>
        <input
          type="number"
          min={0}
          max={100}
          className="border rounded px-3 py-2 w-32"
          value={commission}
          onChange={(e) => setCommission(parseFloat(e.target.value) || 0)}
        />
        <button
          type="button"
          className="ml-4 px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50"
          disabled={saving}
          onClick={saveCommission}
        >
          Save commission
        </button>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Terms & Conditions</h2>
        <p className="text-sm text-gray-500 mb-3">Plain text shown in the mobile app when booking.</p>
        <label className="block text-sm text-gray-600 mb-1">English</label>
        <textarea
          className="border rounded w-full min-h-[120px] p-2 mb-4"
          value={termsEn}
          onChange={(e) => setTermsEn(e.target.value)}
        />
        <label className="block text-sm text-gray-600 mb-1">Arabic</label>
        <textarea
          className="border rounded w-full min-h-[120px] p-2 mb-4"
          value={termsAr}
          onChange={(e) => setTermsAr(e.target.value)}
        />
        <button
          type="button"
          className="px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50"
          disabled={saving}
          onClick={saveTerms}
        >
          Save Terms & Conditions
        </button>
      </section>
    </div>
  );
}

const tabs: { id: MainTab; label: string }[] = [
  { id: 'settings', label: 'Settings' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'payments', label: 'Transactions' },
  { id: 'analytics', label: 'Analytics' },
];

export default function ChefOnDemandSettings() {
  const [mainTab, setMainTab] = useState<MainTab>('settings');

  return (
    <div className="p-6 max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Chef On Demand</h1>
        <p className="text-sm text-gray-500 mt-1">
          Bookings and card payments are separate from shop orders. Manage cuisine/specialty catalogs from the sidebar.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setMainTab(t.id)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              mainTab === t.id
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mainTab === 'settings' && <ChefOnDemandPricingSettings />}
      {mainTab === 'bookings' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">All chef bookings</h2>
          <p className="text-sm text-gray-500 mb-4">Home visits & meal prep requests (Mongo collection: chefondemandbookings).</p>
          <ChefBookingsAdminTable />
        </div>
      )}
      {mainTab === 'payments' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Card payment attempts</h2>
          <p className="text-sm text-gray-500 mb-4">Paymob checkout sessions (Mongo: chefbookingpayments). Completed = successful charge.</p>
          <ChefBookingPaymentsAdminTable />
        </div>
      )}
      {mainTab === 'analytics' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Analytics</h2>
          <p className="text-sm text-gray-500 mb-4">Counts, paid revenue, and unpaid pipeline value.</p>
          <ChefBookingAnalyticsPanel />
        </div>
      )}
    </div>
  );
}
